import get from "lodash/get";
import { fetchInfo, createOrder } from "@/api/modules/products/orangetickets";
import { makeUtcDate } from "@/utilities/time";

const InfoModel = function(orderInfo) {
    this.identifier = orderInfo.productTypeIdentifier;
    // Javascript will interpret the result as text
    this.allowedPassengerTypeIds = orderInfo.allowedPassengerTypeIds.map(id =>
        parseInt(id)
    );
    this.maximumNumberOfPassengersPerItem =
        orderInfo.maximumNumberOfPassengersPerItem;
    this.maximumStartValidityDaysFromNow =
        orderInfo.maximumStartValidityDaysFromNow;
};

export const state = {
    info: null
};

export const mutations = {
    updateInfo: (state, payload) => {
        state.info = new InfoModel(payload);
    }
};

export const getters = {
    isEnabled: state => Boolean(state.info)
};

export const actions = {
    fetchInfo: context => {
        return fetchInfo(true)
            .then(response => {
                if (!response.ok) throw new Error(response.responseBody);
                return response.responseBody;
            })
            .then(json => {
                context.commit("updateInfo", json);
                context.commit(
                    "system/addStopProductType",
                    {
                        productType: context.state.info.identifier
                    },
                    { root: true }
                );
            });
    },
    prepareOrder: (
        context,
        {
            trip,
            product,
            refundable,
            calculatedPriceOre,
            seatQuantity,
            seatType
        }
    ) => {
        const {
            allowedPassengerTypeIds,
            maximumStartValidityDaysFromNow,
            maximumNumberOfPassengersPerItem
        } = context.state.info;
        const serverTime = makeUtcDate(
            context.rootGetters.getServerDateAsMoment
        );
        const periodStart = makeUtcDate(serverTime)
            .add(-1, "days")
            .endOf("day");
        const periodEnd = makeUtcDate(serverTime)
            .add(maximumStartValidityDaysFromNow, "days")
            .startOf("day")
            .add(-1, "second");

        if (trip) {
            if (!product)
                throw new Error(
                    "cannot prepare orange ticket order without a productId"
                );
            if (!calculatedPriceOre)
                throw new Error(
                    "cannot prepare orange ticket order without calculatedPriceOre"
                );

            context.commit(
                "order/setDetails",
                {
                    trip,
                    productId: product.id,
                    refundable,
                    calculatedPriceOre,
                    seatTypeId: get(seatType, "id", null),
                    seatProductId: get(seatType, "productId", null),
                    seatQuantity
                },
                { root: true }
            );
        }

        context.commit(
            "passenger/updatePassengers",
            {
                passengerTypeIds: allowedPassengerTypeIds,
                maximumPassengers: maximumNumberOfPassengersPerItem
            },
            { root: true }
        );
        return context.dispatch(
            "date/prepareSelectableDates",
            { periodStart, periodEnd },
            { root: true }
        );
    },
    createOrder: context => {
        const passengers = context.rootGetters["passenger/getPassengers"].map(
            passenger => {
                return {
                    passengerTypeId: passenger.passengerType.id,
                    passengerTypeCount: passenger.count
                };
            }
        );

        const preparedDetails = context.rootGetters["order/getDetails"];

        const {
            trip,
            productId,
            refundable,
            seatTypeId,
            seatProductId,
            seatQuantity
        } = preparedDetails;

        if (!trip)
            throw new Error("cannot create orange ticket order without a trip");
        if (!productId)
            throw new Error(
                "cannot create orange ticket order without a productId"
            );

        return context
            .dispatch(
                "date/validateTripDepartureInstant",
                trip.departureInstant,
                { root: true }
            )
            .then(
                () => {
                    let seating = null;
                    if (seatTypeId && seatProductId && seatQuantity) {
                        seating = {
                            seatTypeId,
                            seatProductVariantId: seatProductId,
                            amountOfSeatsWanted: seatQuantity
                        };
                    }
                    return createOrder(
                        trip.tripId,
                        productId,
                        refundable,
                        passengers,
                        seating
                    )
                        .then(response => {
                            if (response.ok) {
                                return response.responseBody;
                            } else if (
                                response.status === 422 &&
                                response.responseBody.error ===
                                    "TicketNotSellable"
                            ) {
                                throw new Error("TicketNotSellable");
                            } else {
                                throw new Error(JSON.stringify(response));
                            }
                        })
                        .then(({ orderId, priceOre, ...details }) => {
                            context.commit("order/setOrderId", orderId, {
                                root: true
                            });
                            context.commit("order/setPriceOre", priceOre, {
                                root: true
                            });
                            context.commit(
                                "order/setDetails",
                                {
                                    ...preparedDetails,
                                    ...details
                                },
                                { root: true }
                            );
                        });
                },
                () => {
                    throw new Error("InvalidTripDepartureInstant");
                }
            );
    }
};
